import path from 'node:path'
import * as p from '@clack/prompts'
import type { RemoveCommandOptions } from '@core-engine/commands/runtime.js'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import { detectPackageManager } from '@core-engine/utils/package-manager.js'
import { resolveConfig } from '@next-adapter/config.js'
import {
  formatUnknownIntegrationMessage,
  isIntegrationId
} from '@next-adapter/integration-registry.js'
import { removeIntegrations } from '@next-adapter/integration-runtime.js'
import { formatUnknownPluginMessage, isPluginId } from '@next-adapter/plugin-registry.js'
import { removePlugins } from '@next-adapter/plugin-runtime.js'

export async function runRemoveCommand(
  items: string[],
  options: RemoveCommandOptions
): Promise<void> {
  const removeIntegrationsMode = Boolean(options.integration)
  if (!removeIntegrationsMode && items.includes('core')) {
    p.log.error('The core Admin cannot be removed.')
    process.exit(1)
  }

  const pluginIds = items.filter(isPluginId)
  const integrationIds = items.filter(isIntegrationId)

  if (!removeIntegrationsMode && integrationIds.length > 0) {
    p.log.error(
      `Integration IDs require --integration. Run \`betterstart remove --integration ${integrationIds.join(' ')}\`.`
    )
    process.exit(1)
  }

  if (removeIntegrationsMode && pluginIds.length > 0) {
    p.log.error(`Plugin IDs cannot be removed with --integration: ${pluginIds.join(', ')}`)
    process.exit(1)
  }

  const invalidItems = removeIntegrationsMode
    ? items.filter((integrationId) => !isIntegrationId(integrationId))
    : items.filter((pluginId) => !isPluginId(pluginId))
  if (invalidItems.length > 0) {
    p.log.error(
      removeIntegrationsMode
        ? formatUnknownIntegrationMessage(invalidItems)
        : formatUnknownPluginMessage(invalidItems)
    )
    process.exit(1)
  }

  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const config = await resolveConfig(cwd)
  const pm = detectPackageManager(cwd)

  if (!options.force) {
    const confirmed = await p.confirm({
      message: `Remove ${removeIntegrationsMode ? 'integration' : 'plugin'}${items.length === 1 ? '' : 's'} ${items.join(', ')}?`,
      initialValue: false
    })

    if (p.isCancel(confirmed) || !confirmed) {
      p.cancel(`${removeIntegrationsMode ? 'Integration' : 'Plugin'} removal cancelled.`)
      process.exit(0)
    }
  }

  if (removeIntegrationsMode) {
    const result = await removeIntegrations({
      cwd,
      config,
      pm,
      integrationIds
    })

    writeConfigFile(cwd, result.config)

    if (result.removed.length === 0) {
      p.outro('No integrations were removed.')
      return
    }

    if (result.warnings.length > 0) {
      p.note(result.warnings.join('\n'), 'Warnings')
    }

    p.outro(
      `Removed integration${result.removed.length === 1 ? '' : 's'}: ${result.removed.join(', ')}`
    )
    return
  }

  const result = await removePlugins({
    cwd,
    config,
    pm,
    pluginIds
  })

  writeConfigFile(cwd, result.config)

  if (result.removed.length === 0) {
    p.outro('No plugins were removed.')
    return
  }

  if (result.warnings.length > 0) {
    p.note(result.warnings.join('\n'), 'Warnings')
  }

  p.outro(`Removed plugin${result.removed.length === 1 ? '' : 's'}: ${result.removed.join(', ')}`)
}
